/**
 * Created by Ivo Meißner on 21.05.14. (info@overtronic.com)
 *
 * General handler for ajax requests
 *
 * Available Events:
 *
 * 'start': Is triggered when a request is sent to the server
 * 'success': Is triggered when a successful response comes back from the server
 * 'error': Is triggered when the request to the server fails or the server returns an error
 * 'cancel': Is thrown when the request is cancelled
 *
 * The callback functions for `start`, `error`, `cancel` take one argument `requestData` which
 * contains all the necessary data for the request
 * {
 *      route: 'the route name',
 *      routeParams: 'an object of routeParams',
 *      queryParams: 'an object of queryParams',
 *      options: 'an object of additional options'
 *      xhr: 'the jqXHR object, see jQuery Documentation'
 * }
 *
 * The callback function for `success` takes `responseContent` and `requestData` as arguments.
 *
 * Example usage:
 * var requestHandler = new MultiRequestHandler();
 *
 * requestHandler.on('success', function(responseContent, requestData){
 *      // Only trigger certain action on specific route
 *      if (requestData.route == 'myroute'){
 *          //Load successful
 *
 *      }
 * });
 * requestHandler.on('error', function(requestData){
 *      //Error handling
 * });
 *
 */

define(
    [   'jQuery',
        'library/fcse-events/eventDispatcher',
        'log',
        'library/fcse-util/url',
        'Routing',
        'RoutingData', 'underscore'],
    function ($, EventDispatcher, Log, Url, Routing, RoutingData, _) {
        /**
         * Constructor for general event handler
         * @constructor
         */
        var MultiRequestHandler = function(options){
            // Extend EventDispatcher
            EventDispatcher.apply(this, arguments);

            // Determine locale
            var url = new Url();
            var locale = url.getLocale();

            // Default options for the request handler
            var defaultOptions = {
                method: 'GET',
                locale: locale,
                defaultRouteParams: {}
            };
            this.addOptions(defaultOptions, options);

            // Initialize object for pending requests
            this._pendingRequests = {};

            // Register events
            this.registerEvent('success');
            this.registerEvent('error');
            this.registerEvent('start');
            this.registerEvent('cancel');
        }

        /**
         * Extend EventDispatcher prototype
         * @type {void|*}
         */
        MultiRequestHandler.prototype = $.extend(Object.create(EventDispatcher.prototype), {
            /**
             * Sends
             * @param string route The name of the route as defined by Routing
             * @param object queryParams An object of additional query parameters to be sent to the server
             * @param object routeParams An object of parameters to configure the route as defined by Routing
             * @param object options An object of configuration options
             * @return object The requestData {
             *      route: 'the route name',
             *      routeParams: 'an object of routeParams',
             *      queryParams: 'an object of queryParams',
             *      options: 'an object of additional options'
             *      xhr: 'the jqXHR object, see jQuery Documentation'
             * }
             */
            send: function(route, queryParams, routeParams, options){
                var self = this;
                routeParams = $.extend(this._getDefaultRouteParams(), routeParams || {});

                // Generate URL for route.
                var url = Routing.generate(route, routeParams);

                // Create request data object to be stored in request registry
                var requestData = {
                    route: route,
                    routeParams: routeParams,
                    queryParams: queryParams,
                    options: options
                };

                var configOptions = $.extend(Object.create(self.getOptions()), options || {});

                // Send ajax request
                var xhr = $.ajax(url, {
                    data: queryParams,
                    type: configOptions.method
                })
                    .done(function(content, textStatus, jqXhr){
                        self._onAjaxSuccess(content, jqXhr, requestData);
                    })
                    .fail(function(jqXhr){
                        self._onAjaxError(jqXhr, requestData);
                    });

                // Trigger ajax start
                self._onAjaxStart(xhr, requestData);

                // Attach XHR object to requestData
                requestData['xhr'] = xhr;

                // Store pending request in registry
                this._addPendingRequest(route, requestData);

                // Return the request object
                return requestData;
            },

            /**
             * Cancels all pending requests for the given route
             * @param route string
             */
            cancel: function(route){
                var self = this;
                if (this._pendingRequests.hasOwnProperty(route)){
                    // Cancel all requests for the specified route
                    $.each(this._pendingRequests[route], function(index, requestData){
                        requestData.xhr.abort();
                        self.dispatchEvent('cancel', requestData);
                    });
                }
            },

            /**
             * Cancels all pending requests
             */
            cancelAll: function(){
                var self = this;
                $.each(this._pendingRequests, function(key, value){
                    $.each(self._pendingRequests[key], function(index, requestData){
                        requestData.xhr.abort();
                        self.dispatchEvent('cancel', requestData);
                    });
                });
            },

            /**
             * Returns an object of all pending requests
             */
            getPendingRequests: function(){
                return this._pendingRequests;
            },

            /**
             * Saves the request in the pending request registry
             *
             * @param route string
             * @param data object
             * @private
             */
            _addPendingRequest: function(route, data){
                if (this._pendingRequests.hasOwnProperty(route)){
                    this._pendingRequests[route].push(data);
                } else {
                    this._pendingRequests[route] = [data];
                }
            },

            /**
             * Handler for ajax success
             * @param content
             * @param jqXhr
             * @param requestData
             * @private
             */
            _onAjaxSuccess: function(content, jqXhr, requestData){
                this._removePendingRequest(requestData.route, jqXhr);

                this.dispatchEvent('success', content, requestData);
            },

            /**
             * Handler for ajax error
             * @param jqXhr
             * @param requestData
             * @private
             */
            _onAjaxError: function(jqXhr, requestData){
                this._removePendingRequest(requestData.route, jqXhr);

                var log = new Log();
                log.error('Route: ' + requestData.route + ' HTTP error occurred. Status: ' + jqXhr.status + ' Text: ' + jqXhr.statusText);

                this.dispatchEvent('error', requestData);
            },

            /**
             * Handler for ajax start
             * @param jqXhr
             * @param requestData
             * @private
             */
            _onAjaxStart: function(jqXhr, requestData){
                this.dispatchEvent('start', requestData);
            },

            /**
             * Removes a pending request from registry
             * @param route
             * @param jqXhr
             * @private
             */
            _removePendingRequest: function(route, jqXhr){
                var self = this;
                if (this._pendingRequests.hasOwnProperty(route)){
                    var indexDelete = -1;
                    $.each(this._pendingRequests[route], function(index, requestData){
                        if (requestData.xhr == jqXhr){
                            indexDelete = index;
                        }
                    });
                    if (indexDelete >= 0){
                        self._pendingRequests[route].splice(indexDelete, 1);

                        if (self._pendingRequests[route].length === 0){
                            delete self._pendingRequests[route];
                        }
                    }
                }
            },

            /**
             * Returns an object of default route params
             * @private
             */
            _getDefaultRouteParams: function(){
                // Create params
                var params = {};
                params['_locale'] = this.option('locale');
                if (this.option('defaultRouteParams')) {
                    params = $.extend({}, this.option('defaultRouteParams'), params);
                }
                return params;
            },

            /**
             * Internal variable to store configuration values
             */
            _options: {},

            /**
             * Adds options to the MultiRequestHandler.
             * The parameter options will overwrite the defaults
             * @param {object} defaults
             * @param {object|undefined} options
             * @returns {undefined}
             */
            addOptions: function(defaults, options){
                this._options = $.extend({}, this.getOptions(), defaults, options || {});
            },

            /**
             * Returns an object of all configuration options of the MultiRequestHandler
             * @returns {undefined|Object}
             */
            getOptions: function(){
                return this._options;
            },

            /**
             * Gets or sets option
             * @param name
             * @param value optional
             */
            option: function (name, value) {
                if (!_.isUndefined(value)) {
                    this._options[name] = value;
                }
                return this._options[name];
            }
        });

        return MultiRequestHandler;
    }
);



//////////////////
// WEBPACK FOOTER
// ./~/fcse-request/multiRequestHandler.js
// module id = 58
// module chunks = 5 6 7 8 9 10 11 12 13 14 15 16 17 18 19 20 21 22 23